/**
 * dynamicTriage.js - 2026-10-18 (Dr. Tatar: T4 expectant rises when the care they need is not there)
 *
 * Rule: a T1 casualty (needs an immediate lifesaving intervention or surgery and is expected to survive if he gets it) is
 * held in T4 on a day when what he needs is missing: BLOOD (alert RED or BLACK), a SURGEON (the T1 operations of the day above what the Role 2 Basic and the accessible
 * facilities can take, T1 first). The hold ends on the first day the need is met again; the T4 death rate
 * (TRIAGE_CATEGORIES.T4_EXPECTANT.mortality_rate) leaves the part that is still alive when the care returns. Doctrine: T1 may drop
 * to T2 after temporary treatment and T4 is declared by the commander or medical adviser (AMedP-1.10; TCCC triage supplement).
 *
 * STATUS of the values
 *  - T1 share that needs blood: DERIVED. Transfused share of admitted casualties 14 to 26 percent (JTS Damage Control Resuscitation
 *    guideline: 14 percent of Role 3 admissions 2003-2012; Role 2 DoD registry 17 percent; US and Coalition registry 25.6 percent; NATO
 *    planning factor 20 percent), T1 about 25 percent of the wounded, nearly all blood use sits in T1: 55 to 75 percent of T1. The only
 *    series of serious casualties found (Balad 2008, three multiple casualty incidents, 50 patients) had 48 percent transfused.
 *    Central 60, low 48, high 75 (Dr. Tatar accepted 2026-10-18).
 *  - Blood need by wound region: transfused share of casualties with a wound of that region: head 22, trunk 21, extremity 13
 *    (Payne, Meyer, Berne, LA County-USC 1990-91, 970 gunshot patients), thorax 52 (Iraq and Afghanistan, 3,937 transfused thoracic
 *    patients). Abdomen 19 is DERIVED (it makes the combat wound mix reproduce the 20 percent planning factor). Burns and multi-region
 *    are EDUCATED GUESS.
 *  - Hold by alert: BLACK all, RED half: EDUCATED GUESS.
 *
 * 2026-10-18 (Dr. Tatar: regional literature, not only military; denominators of the surgical population): switch
 * T1_BLOOD_NEED_BY_REGION_TABLE = true replaces the all-wounds regional rates above with the share of T1 casualties with a wound of the
 * region who need blood, T1_BLOOD_NEED_BY_REGION (sources and status per region below). The former values stay (switch false).
 */
// true: share of T1 that needs blood = wound mix x T1_BLOOD_NEED_BY_REGION. false: former reading (central 60 percent scaled by the mix).
export const T1_BLOOD_NEED_BY_REGION_TABLE = true;

/**
 * Share of the T1 casualties with a wound of the region who need blood (massive transfusion or lifesaving transfusion).
 *  thorax 0.65   Massive haemothorax: 13 of 20 (65 percent) received a massive transfusion (Milano trauma centre, severely injured,
 *                n = 139, PMC7601146). Military: 52 percent of thoracic trauma patients transfused (PMC4335151). Karmy-Jones: urgent thoracotomy
 *                for bleeding, mortality 17 percent penetrating and 67 percent blunt (JAMA Surg 2001). DERIVED.
 *  abdomen 0.50  Positive abdominal E-FAST (haemoperitoneum): 28 of 59 (47.5 percent) massive transfusion (same cohort); laparotomy
 *                patients 36.4 percent transfused (blunt 46.9, penetrating 30; Addis Ababa, PMC6689702); stable penetrating with peritonitis
 *                39 percent (Springer review); hypotensive penetrating 11.2 units in 24 hours (control arm, cell salvage trial). T1 = unstable,
 *                so the upper part of the range. DERIVED.
 *  extremity 0.70 T1 extremity = amputation or vascular injury with uncontrolled bleeding: limb amputation 9 of 10 (90 percent) massive
 *                transfusion (n = 10, wide interval); vascular trauma 76 percent transfused against 16 percent of general trauma (review
 *                without a stated data source); DoD registry: serious extremity injury odds ratio 6.59 for massive transfusion. EDUCATED GUESS
 *                between the two. Pelvic fracture (54.4 percent massive transfusion, 37 of 68) falls under multi.
 *  head_neck 0.10 Blood is not the lifesaving need of a head-injured T1 (craniotomy is). Transfusion in isolated severe TBI marks severity
 *                (mortality 47 percent in the transfused TQIP group, first unit +20 percent) and a head injury does not raise the red cell need
 *                of severely injured patients (median 10 units with and without head injury, RESTRIC post hoc, PMC13079471). EDUCATED GUESS.
 *  burns 0.10    Fluids and plasma more than red cells. EDUCATED GUESS.
 *  multi 0.56    Severely injured patients of the Milano cohort: 78 of 139 (56 percent) received a massive transfusion. DERIVED.
 */
export const T1_BLOOD_NEED_BY_REGION = Object.freeze({
  extremity: 0.70, head_neck: 0.10, thorax: 0.65, abdomen: 0.50, burns: 0.10, multi: 0.56
});
export const DYNAMIC_T4_STATUS = Object.freeze({
  status: 'DERIVED',
  basis: 'T1 share needing blood from transfused shares of admitted casualties (14 to 26 percent) and the T1 share of the wounded; region rates from Payne 1990-91 and the Iraq/Afghanistan thoracic series; abdomen derived; hold by alert educated guess.'
});

/** Share of T1 that needs blood: central, low and high case. */
export const T1_BLOOD_NEED_SHARE = Object.freeze({ central: 0.60, low: 0.48, high: 0.75 });

/** Transfused share of casualties with a wound of the region (wound mix regions of the calculator). */
export const REGION_BLOOD_RATE = Object.freeze({
  extremity: 0.13,
  head_neck: 0.22,
  thorax: 0.52,
  abdomen: 0.19,    // DERIVED
  burns: 0.13,      // EDUCATED GUESS (as extremity: fluids and plasma more than red cells)
  multi: 0.52       // EDUCATED GUESS (as the highest sourced region)
});

/** Wound mix (Iraq and Afghanistan, 2005-2009 JTTR wounded not killed) at which T1_BLOOD_NEED_SHARE applies. */
export const REFERENCE_WOUND_MIX = Object.freeze({ extremity: 0.52, head_neck: 0.28, thorax: 0.10, abdomen: 0.10, burns: 0, multi: 0 });

/** Share of the T1 that need blood who are held in T4 by blood alert level. */
export const BLOOD_HOLD_BY_ALERT = Object.freeze({ GREEN: 0, YELLOW: 0, RED: 0.5, BLACK: 1.0 });

const mixRate = (mix) => Object.keys(REGION_BLOOD_RATE).reduce((s, r) => s + (mix[r] || 0) * REGION_BLOOD_RATE[r], 0);
const REFERENCE_RATE = mixRate(REFERENCE_WOUND_MIX);

/**
 * Share of T1 that needs blood for this force's wound mix (a mix with more thorax and abdomen needs more).
 * @param {Object} woundDistribution shares by region (sum about 1)
 * @param {number} central share at the reference mix
 */
export function t1BloodNeedShare(woundDistribution, central = T1_BLOOD_NEED_SHARE.central) {
  if (!woundDistribution) throw new Error('dynamicTriage.t1BloodNeedShare: woundDistribution is required');
  if (T1_BLOOD_NEED_BY_REGION_TABLE) {
    const sum = Object.keys(T1_BLOOD_NEED_BY_REGION).reduce((t, r) => t + (woundDistribution[r] || 0), 0);
    if (!(sum > 0)) throw new Error('dynamicTriage.t1BloodNeedShare: woundDistribution has no shares');
    return Object.keys(T1_BLOOD_NEED_BY_REGION).reduce((t, r) => t + ((woundDistribution[r] || 0) / sum) * T1_BLOOD_NEED_BY_REGION[r], 0);
  }
  const total = Object.keys(REGION_BLOOD_RATE).reduce((s, r) => s + (woundDistribution[r] || 0), 0);
  if (!(total > 0)) throw new Error('dynamicTriage.t1BloodNeedShare: woundDistribution has no shares');
  const rate = Object.keys(REGION_BLOOD_RATE).reduce((s, r) => s + ((woundDistribution[r] || 0) / total) * REGION_BLOOD_RATE[r], 0);
  return Math.min(1, central * rate / REFERENCE_RATE);
}

/** Share of T1 held in T4 for lack of blood. */
export function t1HeldForBlood({ alert, woundDistribution, central }) {
  const hold = BLOOD_HOLD_BY_ALERT[alert];
  if (typeof hold !== 'number') throw new Error(`dynamicTriage.t1HeldForBlood: unknown blood alert '${alert}'`);
  return hold === 0 ? 0 : hold * t1BloodNeedShare(woundDistribution, central);
}

/**
 * Share of T1 held in T4 for lack of an operating team. Surgeons take T1 first, so the day's capacity (the Role 2 Basic up to its crisis
 * limit plus what the accessible facilities can take) goes to T1 before anyone else: the T1 cases above it cannot be operated on.
 * (Backlog is lower priority: it does not take T1's place.)
 */
export function t1HeldForSurgeon({ t1Cases, capacity }) {
  if (!(t1Cases > 0)) return 0;
  return Math.min(1, Math.max(0, t1Cases - capacity) / t1Cases);
}

/** Two independent reasons: the casualty is held if either applies. */
export const combineHolds = (...holds) => 1 - holds.reduce((p, h) => p * (1 - Math.min(1, Math.max(0, h))), 1);
